import { createReducer, Action, on } from '@ngrx/store';
import { SuiSortState as Sort } from '@semeris/ui';
import { difference, mergeWith } from 'lodash-es';

import { TermExplorerActions } from './term-explorer.actions';
import { TermExplorerState } from './term-explorer.state';
import { mergeSynonymGroupCounts } from '../../features/deal/utils/term-explorer-list';
import { measureReducer } from '../../features/shared/utils/runtime-reducer-stats';

export const initialState: TermExplorerState = {
  loadingFinal: false,
  loadingOriginal: false,
  focusMode: false,
  groupSynonyms: true,
  searchTerm: '',
  sort: {
    active: 'definedTerm',
    direction: 'asc',
  } as Sort,
};

const featureReducer = measureReducer(
  createReducer(
    initialState,
    on(TermExplorerActions.LoadFinalDocumentTerms, (state, action) => ({
      ...state,
      loadingFinal: true,
      finalTerms: null,
      finalDocumentId: action.documentId,
      error: undefined,
    })),
    on(TermExplorerActions.LoadOriginalDocumentTerms, (state, action) => ({
      ...state,
      loadingOriginal: true,
      originalTerms: null,
      originalDocumentId: action.documentId,
      error: undefined,
    })),
    on(TermExplorerActions.LoadFinalDocumentTermsFail, (state, action) => ({
      ...state,
      loadingFinal: false,
      error: action.error,
    })),
    on(TermExplorerActions.LoadOriginalDocumentTermsFail, (state, action) => ({
      ...state,
      loadingOriginal: false,
      error: action.error,
    })),
    on(TermExplorerActions.LoadDefinedTermOccurenceFail, (state, action) => ({
      ...state,
      loadingOccurencies: false,
      error: action.error,
    })),
    on(TermExplorerActions.LoadFinalDocumentTermsSuccess, (state, action) => ({
      ...state,
      loadingFinal: false,
      finalTerms: action.list,
    })),
    on(TermExplorerActions.LoadOriginalDocumentTermsSuccess, (state, action) => ({
      ...state,
      loadingOriginal: false,
      originalTerms: action.list,
    })),
    on(TermExplorerActions.CleanFinalDocumentTerms, (state) => ({
      ...state,
      loadingFinal: false,
    })),
    on(TermExplorerActions.CleanOriginalDocumentTerms, (state, action) => ({
      ...state,
      loadingOriginal: false,
      originalTerms: null,
      focusMode: false,
    })),
    on(TermExplorerActions.LoadDefinedTermOccurences, (state, action) => ({
      ...state,
      transactionIdsToDownload: action.transactionIds,
      occurenceTransactionIds: action.transactionIds,
      occurencesByFilter: {},
      synonymGroupsByFilter: {},
      loadingOccurencies: !!action.transactionIds?.length,
    })),
    on(TermExplorerActions.LoadDefinedTermOccurenceChunkSuccess, (state, action) => ({
      ...state,
      occurencesByFilter: mergeWith({ ...state.occurencesByFilter }, action.occurences, (left, right) => (+left || 0) + (+right || 0)),
      synonymGroupsByFilter: mergeSynonymGroupCounts(state.synonymGroupsByFilter, action.synonymGroups),
      transactionIdsToDownload: difference(state.transactionIdsToDownload, action.transactionIds),
      loadingOccurencies: false,
    })),
    on(TermExplorerActions.SetTermScope, (state, action) => ({
      ...state,
      termScope: action.termScope,
    })),
    on(TermExplorerActions.SetSort, (state, action) => ({ ...state, sort: action.sort })),
    on(TermExplorerActions.SetFocusMode, (state, action) => ({
      ...state,
      focusMode: action.focusMode,
    })),
    on(TermExplorerActions.SetGroupSynonyms, (state, action) => ({
      ...state,
      groupSynonyms: action.groupSynonyms,
    })),
    on(TermExplorerActions.SetSearch, (state, action) => ({
      ...state,
      searchTerm: action.searchTerm,
    })),
  ),
);

export function reducer(state: TermExplorerState | undefined, action: Action) {
  return featureReducer(state, action);
}
